import {
  ChangeEvent,
  ChangeEventHandler,
  ComponentProps,
  FocusEvent,
  useCallback,
  useRef,
} from 'react';
import { sprintf } from 'sprintf-js';

import { ClearButton } from 'components/Input/ClearButton';
import { Input } from 'components/Input/Input';
import { InputProps } from 'components/Input/InputProps';
import { ClickOutside } from 'utils/ClickOutside';

import {
  AutocompleteHolder,
  AutocompleteSuggestion,
  AutocompleteSuggestions,
  AutocompleteSuggestionsHolder,
  AutocompleteTitle,
} from './Autocomplete.styled';
import { useAutocomplete } from './useAutocomplete';

type Props = {
  inputProps: InputProps;
  onChange: ChangeEventHandler;
  onClear?: () => void;
  onSuggestionSelected?: (suggestion: string) => void;
  openOnFocus: boolean;
  presentation: ComponentProps<typeof Input>['styleVariant'];
  qaId?: string;
  shouldShowSuggestions: (inputValue: string) => boolean;
  suggestions: string[];
  title: string;
};

export function Autocomplete({
  inputProps,
  onChange,
  onClear,
  onSuggestionSelected,
  openOnFocus = false,
  presentation,
  qaId,
  shouldShowSuggestions,
  suggestions,
  title,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { defaultValue, id, maxLength, placeholder } = inputProps;

  const {
    setShowSuggestions,
    showSuggestions,
    suggestionsRef,
    handleInputKeyDown,
    handleInputChange,
    handleInputFocus,
    handleSuggestionClick,
  } = useAutocomplete(
    inputRef,
    openOnFocus,
    shouldShowSuggestions,
    onSuggestionSelected,
  );

  const clearInput = useCallback(() => {
    if (inputRef.current) inputRef.current.value = '';
    onClear?.();
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [onClear]);

  const suggestionsContainerId = 'autocomplete';

  return (
    <ClickOutside onClickOutside={() => setShowSuggestions(false)}>
      <AutocompleteHolder onKeyDown={handleInputKeyDown}>
        <Input
          key={defaultValue}
          inputProps={{
            ref: inputRef,
            id,
            name: 'q',
            placeholder,
            defaultValue,
            qaId,
            maxLength,
            onChange: (e: ChangeEvent<HTMLInputElement>) => {
              handleInputChange(e);
              onChange(e);
            },
            onFocus: (e: FocusEvent<HTMLInputElement>) => {
              handleInputFocus(e);
            },
            ariaExpanded: showSuggestions,
            autoComplete: 'off',
            ariaControls: suggestionsContainerId,
            role: 'combobox',
          }}
          suffix={
            <ClearButton
              ariaLabel="Clear search input"
              onClear={clearInput}
              qaId="search-input-clear"
            />
          }
          hideSuffixWhenEmpty
          styleVariant={presentation}
          affixVariant="transparent"
        />

        {showSuggestions && suggestions.length > 0 && (
          <AutocompleteSuggestionsHolder id={suggestionsContainerId}>
            <AutocompleteTitle>{title}</AutocompleteTitle>
            <AutocompleteSuggestions
              data-qa-id="autocomplete-suggestions"
              role="listbox"
              ref={suggestionsRef}
            >
              {suggestions.map((suggestion: string, index) => (
                <AutocompleteSuggestion
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  aria-label={sprintf(
                    getText('Popular Search Term - %(suggestion)s'),
                    { suggestion },
                  )}
                  data-qa-id="autocomplete-suggestion"
                  fullWidth
                  icon={{ type: 'normalized', name: 'search' }}
                  onClick={() => handleSuggestionClick(suggestion)}
                  noPadding
                  variant="tertiary-grey"
                >
                  {suggestion}
                </AutocompleteSuggestion>
              ))}
            </AutocompleteSuggestions>
          </AutocompleteSuggestionsHolder>
        )}
      </AutocompleteHolder>
    </ClickOutside>
  );
}
